import { createSignal, createMemo, createEffect, For, Show, onCleanup } from 'solid-js';
import './ThemedTable.css';
import { gold, qty, relTime, tsMs } from '../../lib/format.js';
import { pickUser, pickGuild } from '../../lib/picks.js';

/**
 * ThemedTable — Premier Reusable Dwemer / Stained-Glass Data Table.
 *
 * Features:
 *   - Drag-to-reorder columns with localStorage persistence
 *   - Directional sort headers (▲ / ▼) with automatic numeric/currency/date comparators
 *   - Column visibility toggle picker (👁️ Columns)
 *   - Integrated instant search filter across rows
 *   - Smooth glassmorphic rows with hover illumination
 */
export default function ThemedTable(props) {
  const storagePrefix = () => props.storageKey ? `v2_tt_${props.storageKey}_` : null;

  // ── Column state & order ─────────────────────────────────────────────
  const initialKeys = () => (props.columns || []).map(c => c.key);

  const [columnOrder, setColumnOrder] = createSignal((() => {
    if (typeof localStorage !== 'undefined' && storagePrefix()) {
      try {
        const saved = localStorage.getItem(storagePrefix() + 'order');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length) {
            const currentKeys = initialKeys();
            const filtered = parsed.filter(k => currentKeys.includes(k));
            for (const k of currentKeys) {
              if (!filtered.includes(k)) filtered.push(k);
            }
            return filtered;
          }
        }
      } catch { /* tolerate */ }
    }
    return initialKeys();
  })());

  // Hidden columns set
  const [hiddenCols, setHiddenCols] = createSignal((() => {
    if (typeof localStorage !== 'undefined' && storagePrefix()) {
      try {
        const saved = localStorage.getItem(storagePrefix() + 'hidden');
        if (saved) return new Set(JSON.parse(saved));
      } catch { /* tolerate */ }
    }
    const defHidden = new Set();
    for (const c of props.columns || []) {
      if (c.defaultVisible === false) defHidden.add(c.key);
    }
    return defHidden;
  })());

  // Save order & hidden to localStorage
  createEffect(() => {
    if (typeof localStorage !== 'undefined' && storagePrefix()) {
      try {
        localStorage.setItem(storagePrefix() + 'order', JSON.stringify(columnOrder()));
        localStorage.setItem(storagePrefix() + 'hidden', JSON.stringify([...hiddenCols()]));
      } catch { /* tolerate */ }
    }
  });

  // Re-sync if props.columns changes dynamically
  createEffect(() => {
    const keys = initialKeys();
    setColumnOrder(prev => {
      const filtered = prev.filter(k => keys.includes(k));
      for (const k of keys) {
        if (!filtered.includes(k)) filtered.push(k);
      }
      return filtered;
    });
  });

  // Map of key -> column definition
  const colMap = createMemo(() => {
    const m = new Map();
    for (const c of props.columns || []) m.set(c.key, c);
    return m;
  });

  // Active ordered visible columns
  const visibleColumns = createMemo(() => {
    const order = columnOrder();
    const hidden = hiddenCols();
    const m = colMap();
    return order
      .filter(k => !hidden.has(k) && m.has(k))
      .map(k => m.get(k));
  });

  // ── Drag & Drop Column Reordering ────────────────────────────────────
  const [draggingKey, setDraggingKey] = createSignal(null);
  const [overKey, setOverKey] = createSignal(null);

  function handleDragStart(e, key) {
    setDraggingKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  }

  function handleDragOver(e, key) {
    e.preventDefault();
    if (draggingKey() && draggingKey() !== key) {
      setOverKey(key);
    }
  }

  function handleDragLeave(key) {
    if (overKey() === key) setOverKey(null);
  }

  function handleDrop(e, targetKey) {
    e.preventDefault();
    const sourceKey = draggingKey();
    if (!sourceKey || sourceKey === targetKey) {
      setDraggingKey(null);
      setOverKey(null);
      return;
    }

    setColumnOrder(prev => {
      const next = [...prev];
      const srcIdx = next.indexOf(sourceKey);
      const tgtIdx = next.indexOf(targetKey);
      if (srcIdx !== -1 && tgtIdx !== -1) {
        next.splice(srcIdx, 1);
        next.splice(tgtIdx, 0, sourceKey);
      }
      return next;
    });

    setDraggingKey(null);
    setOverKey(null);
  }

  function handleDragEnd() {
    setDraggingKey(null);
    setOverKey(null);
  }

  // ── Sorting ──────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = createSignal(props.defaultSortKey || props.columns?.[0]?.key || '');
  const [sortDir, setSortDir] = createSignal(props.defaultSortDir || 'desc');

  function toggleSort(col) {
    if (col.sortable === false) return;
    if (sortKey() === col.key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      const isNumeric = col.type === 'number' || col.type === 'currency' || col.type === 'time';
      setSortDir(isNumeric ? 'desc' : 'asc');
    }
  }

  // ── Search & Filter ──────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = createSignal('');

  const filteredRows = createMemo(() => {
    const raw = props.rows || [];
    const term = searchTerm().trim().toLowerCase();
    if (!term) return raw;

    const fields = props.searchFields || props.columns?.map(c => c.key) || [];
    return raw.filter(row => {
      for (const f of fields) {
        const val = typeof f === 'function' ? f(row) : row[f];
        if (val != null && String(val).toLowerCase().includes(term)) return true;
      }
      return false;
    });
  });

  const sortedRows = createMemo(() => {
    const list = [...filteredRows()];
    const k = sortKey();
    if (!k) return list;

    const col = colMap().get(k);
    if (!col || col.sortable === false) return list;

    const dir = sortDir() === 'asc' ? 1 : -1;

    return list.sort((a, b) => {
      if (typeof col.sortFn === 'function') {
        return col.sortFn(a, b, dir);
      }
      const va = typeof col.getValue === 'function' ? col.getValue(a) : a[k];
      const vb = typeof col.getValue === 'function' ? col.getValue(b) : b[k];

      if (col.type === 'string' || (!col.type && (typeof va === 'string' || typeof vb === 'string'))) {
        return String(va || '').localeCompare(String(vb || '')) * dir;
      }
      return ((Number(va) || 0) - (Number(vb) || 0)) * dir;
    });
  });

  // ── Column Picker Popover ───────────────────────────────────────────
  const [colMenuOpen, setColMenuOpen] = createSignal(false);

  function toggleColVisibility(key) {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  let menuRef;
  const onDocClick = (e) => {
    if (colMenuOpen() && menuRef && !menuRef.contains(e.target)) {
      setColMenuOpen(false);
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('click', onDocClick);
    onCleanup(() => document.removeEventListener('click', onDocClick));
  }

  return (
    <div class={`v2-tt-wrap${props.compact ? ' is-compact' : ''}`}>
      {/* ── Toolbar: Search bar + Column Picker + Extra Controls ── */}
      <div class="v2-tt-toolbar">
        <Show when={props.searchable !== false}>
          <div class="v2-tt-search-wrap">
            <span class="v2-tt-search-icon">🔍</span>
            <input
              type="text"
              class="v2-tt-search-input"
              placeholder={props.searchPlaceholder || 'Filter rows...'}
              value={searchTerm()}
              onInput={e => setSearchTerm(e.currentTarget.value)}
            />
            <Show when={searchTerm()}>
              <button
                type="button"
                class="v2-tt-search-clear"
                onClick={() => setSearchTerm('')}
                title="Clear filter"
              >✕</button>
            </Show>
          </div>
        </Show>

        {/* Row count pill */}
        <div class="v2-tt-meta-badge">
          {qty(sortedRows().length)}
          <Show when={filteredRows().length !== (props.rows?.length || 0)}>
            <span> / {qty(props.rows?.length || 0)}</span>
          </Show>
          <span> rows</span>
        </div>

        {/* Extra controls slot */}
        <Show when={props.extraControls}>
          {props.extraControls}
        </Show>

        {/* Column Picker Button */}
        <div class="v2-tt-col-menu-wrap" ref={menuRef}>
          <button
            type="button"
            class={`v2-tt-btn${colMenuOpen() ? ' is-active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setColMenuOpen(v => !v); }}
            title="Configure visible columns"
          >
            👁️ Columns ({visibleColumns().length})
          </button>
          <Show when={colMenuOpen()}>
            <div class="v2-tt-col-dropdown">
              <For each={props.columns || []}>{(col) => (
                <label class="v2-tt-col-opt">
                  <input
                    type="checkbox"
                    checked={!hiddenCols().has(col.key)}
                    disabled={col.canHide === false}
                    onChange={() => toggleColVisibility(col.key)}
                  />
                  <span>{col.label || col.key}</span>
                </label>
              )}</For>
            </div>
          </Show>
        </div>
      </div>

      {/* ── Scrollable Table ── */}
      <div class="v2-tt-scroll">
        <table class="v2-tt-table">
          <thead class="v2-tt-thead">
            <tr>
              <For each={visibleColumns()}>{(col) => {
                const isSortable = () => col.sortable !== false;
                const isSorted = () => sortKey() === col.key;
                const alignCls = () => `v2-tt--${col.align || 'left'}`;
                const isDragSrc = () => draggingKey() === col.key;
                const isDropTgt = () => overKey() === col.key;

                return (
                  <th
                    class={`v2-tt-th ${alignCls()}`}
                    classList={{
                      'is-sortable': isSortable(),
                      'is-sorted': isSorted(),
                      'is-dragging': isDragSrc(),
                      'is-drop-target': isDropTgt(),
                    }}
                    style={col.width ? { width: col.width } : undefined}
                    draggable={col.canHide !== false}
                    onDragStart={(e) => handleDragStart(e, col.key)}
                    onDragOver={(e) => handleDragOver(e, col.key)}
                    onDragLeave={() => handleDragLeave(col.key)}
                    onDrop={(e) => handleDrop(e, col.key)}
                    onDragEnd={handleDragEnd}
                    onClick={() => toggleSort(col)}
                  >
                    <div class="v2-tt-th-inner">
                      <span class="v2-tt-drag-handle" title="Drag to reorder column">⋮⋮</span>
                      <span>{col.label || col.key}</span>
                      <Show when={isSorted()}>
                        <span class="v2-tt-sort-glyph">
                          {sortDir() === 'asc' ? '▲' : '▼'}
                        </span>
                      </Show>
                    </div>
                  </th>
                );
              }}</For>
            </tr>
          </thead>
          <tbody>
            <Show
              when={sortedRows().length}
              fallback={
                <tr>
                  <td colspan={visibleColumns().length || 1} class="v2-tt-empty">
                    {props.emptyText || 'No matching records.'}
                  </td>
                </tr>
              }
            >
              <For each={sortedRows()}>{(row, idx) => {
                const rowClass = () => typeof props.rowClass === 'function'
                  ? props.rowClass(row, idx())
                  : (props.rowClass || '');

                return (
                  <tr
                    class={`v2-tt-tr ${rowClass()}`}
                    classList={{ 'is-clickable': typeof props.onRowClick === 'function' }}
                    onClick={(e) => props.onRowClick && props.onRowClick(row, e)}
                  >
                    <For each={visibleColumns()}>{(col) => {
                      const alignCls = () => `v2-tt--${col.align || 'left'}`;
                      const rawVal = () => typeof col.getValue === 'function'
                        ? col.getValue(row)
                        : row[col.key];

                      return (
                        <td class={`v2-tt-td ${alignCls()}`}>
                          {typeof col.render === 'function'
                            ? col.render(rawVal(), row, idx())
                            : (rawVal() ?? '—')}
                        </td>
                      );
                    }}</For>
                  </tr>
                );
              }}</For>
            </Show>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Cell Primitives ────────────────────────────────────────────────────────

/** Formatted Gold Cell with gold icon and quality tint */
export function GoldCell(props) {
  return (
    <span class="v2-tt-gold">
      {gold(props.value || 0)}
      <Show when={props.perEach}>
        <span class="v2-tt-dim" style={{ 'font-size': '10px', 'margin-left': '4px' }}>
          {props.perEach}
        </span>
      </Show>
    </span>
  );
}

/** Clickable User @tag chip that opens the UserDetailModal */
export function UserCell(props) {
  const name = () => props.name || props.tag || '—';
  return (
    <span
      class="v2-tt-user-chip"
      onClick={(e) => {
        e.stopPropagation();
        pickUser(name());
      }}
      title={`Inspect member ${name()}`}
    >
      👤 {name()}
    </span>
  );
}

/** Clickable Guild name chip that opens the GuildDetailModal */
export function GuildCell(props) {
  return (
    <span
      class="v2-tt-guild-chip"
      onClick={(e) => {
        e.stopPropagation();
        pickGuild(props.name || props.id);
      }}
      title={`Inspect guild ${props.name}`}
    >
      🛡️ {props.name || '—'}
    </span>
  );
}

/** Status beacon with pulsing dot */
export function StatusBeacon(props) {
  const status = () => props.status || 'offline';
  return (
    <span class="v2-tt-status-beacon">
      <span class={`v2-tt-beacon-dot v2-tt-beacon-dot--${status()}`} />
      <span>{props.label || status()}</span>
    </span>
  );
}

/** Formatted Relative Time Cell with exact ISO hover */
export function RelTimeCell(props) {
  const ms = () => tsMs(props.ts);
  const iso = () => ms() ? new Date(ms()).toISOString() : '—';
  return (
    <span class="v2-tt-mono v2-tt-dim" style={{ 'font-size': '11px' }} title={iso()}>
      {ms() ? relTime(ms()) : '—'}
    </span>
  );
}
